import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Artist } from '../../../components/roster/types';
import { artistsApi } from '../../../repositories/artists';
import ArtistDetailsTab from '../../../components/roster/ArtistDetailsTab';
import ArtistPitchesTab from '../../../components/roster/ArtistPitchesTab';
import AssetsTab from '../../../components/roster/AssetsTab';
import ArtistPlaylist from '../../../components/roster/ArtistPlaylist';
import SegmentedTabs from '../../../components/ui/SegmentedTabs';
import Swal from 'sweetalert2';
import { Skeleton, SkeletonFields } from '../../../components/ui/skeleton';
import Button from '../../../components/ui/Button';
import Breadcrumbs, { HOME_CRUMB } from '../../../components/ui/Breadcrumbs';


const ArtistDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [artist, setArtist] = useState<Artist | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // const initialTab = (searchParams.get('tab') as 'details' | 'pitches' | 'assets') || 'details';
  const [activeTab, setActiveTab] = useState<'details' | 'pitches' | 'assets' | 'playlists'>('details');

  useEffect(() => {
    if (id) {
      loadArtist(id);
    }
  }, [id]);

  // Update activeTab when URL changes
  useEffect(() => {
    const tab = (searchParams.get('tab') as 'details' | 'pitches' | 'assets' | 'playlists') || 'details';
    setActiveTab(tab);
  }, [searchParams]);

  const loadArtist = async (artistId: string) => {
    try {
      setLoading(true);
      const response = await artistsApi.getOne(artistId);
      const artistData = response.data.data || response.data;

      if (artistData) {
        // Map backend field names to frontend field names
        const mappedArtist: Artist = {
          id: artistData.id,
          userId: artistData.userId,
          artistName: artistData.artistName,
          artistUUID: artistData.artistUUID,
          photo: artistData.photo,
          spotifyURI: artistData.spotifyURI,
          appleID: artistData.appleID,
          asin: artistData.asin,
          soundcloudURL: artistData.soundcloudURL,
          tidalURL: artistData.tidalURL,
          bio: artistData.bio,
          hometown: artistData.hometown,
          currentTown: artistData.currentTown,
          country: artistData.country,
          label: artistData.label,
          distributor: artistData.distributor,
          instagram: artistData.instagram,
          tiktok: artistData.tiktok,
          twitter: artistData.twitter,
          facebook: artistData.facebook,
          snapchat: artistData.snapchat,
          spotifyURL: artistData.spotifyURL,
          appleURL: artistData.appleURL,
          amazonURL: artistData.amazonURL,
          deezerURL: artistData.deezerURL,
          listenersLocation: artistData.listenersLocation,
          lyricLanguage: artistData.lyricLanguage,
          vevoRep: artistData.vevoRep,
          vevoRepOther: artistData.vevoRepOther,
          soundCloudContact: artistData.soundCloudContact,
          similarArtists: artistData.similarArtists,
          createdAt: new Date(artistData.createdAt),
          updatedAt: new Date(artistData.updatedAt),
        };

        setArtist(mappedArtist);
      } else {
        Swal.fire({
          title: 'Artist Not Found',
          text: 'The artist you are looking for does not exist.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        navigate('/roster');
      }
    } catch (error: any) {
      console.error('Error loading artist:', error);

      const errorMessage = error.response?.status === 404
        ? 'The artist you are looking for does not exist.'
        : 'Failed to load artist details. Please try again later.';

      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'OK'
      });
      navigate('/roster');
    } finally {
      setLoading(false);
    }
  };

  const handleArtistUpdate = (updatedArtist: Artist) => {
    setArtist(updatedArtist);
  };

  const handleTabChange = (tab: 'details' | 'pitches' | 'assets' | 'playlists') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  }
  if (loading) {
    return (
      <div className="dashboard-container py-2 sm:p-6" aria-busy="true">
        {/* Breadcrumb trail placeholder. */}
        <Skeleton className="w-48 h-4 mb-4" />
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="w-20 h-20 rounded-full shrink-0" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-60" />
            <Skeleton className="w-40 h-4" />
          </div>
        </div>
        <Skeleton className="w-full h-10 mb-6" />
        <div className="p-4 bg-white border rounded-lg border-neutral-200 sm:p-6">
          <SkeletonFields count={9} />
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-black">Artist not found</h2>
          <Button variant="link" size="inline" onClick={() => navigate('/roster')} className="mt-4">
            Return to Roster
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container py-2 sm:p-6">
        <Breadcrumbs
          items={[HOME_CRUMB, { label: 'Roster', to: '/roster' }, { label: artist.artistName }]}
        />

        {/* Tabs */}
        <div className="bg-white border rounded-lg border-neutral-200">
          <div data-tour="artist-tabs-bar">
            <SegmentedTabs
              className="px-2"
              ariaLabel="Artist detail sections"
              value={activeTab}
              onChange={(key) => handleTabChange(key as 'details' | 'pitches' | 'assets' | 'playlists')}
              items={[
                { key: 'details', label: 'Details' },
                { key: 'pitches', label: 'Pitches' },
                { key: 'assets', label: 'Assets', dataTour: 'assets-tab' },
                { key: 'playlists', label: 'Playlists' },
              ]}
            />
          </div>

          <div className="p-6">
            {activeTab === 'details' ? (
              <ArtistDetailsTab
                artist={artist}
                onUpdate={handleArtistUpdate}
              />
            ) : activeTab === 'pitches' ? (
              <ArtistPitchesTab artistId={artist.id} />
            ) : activeTab === 'playlists' ? (
              <ArtistPlaylist artistUUID={artist.artistUUID} artistName={artist.artistName} />
            ) : (
              <AssetsTab artistId={artist.id} />
            )}
          </div>
        </div>
    </div>
  );
};

export default ArtistDetail;